// Generated by GitHub Copilot
import { useEffect, useState } from "react";
import profileFallback from "../data/profileFallback";
import { buildApiUrl } from "../utils/api";

// Generated by GitHub Copilot
/**
 * Build fallback profile data enriched with a live public GitHub snapshot.
 *
 * Security note: this helper only uses public GitHub REST endpoints and does not
 * require client-side secrets.
 *
 * @returns {Promise<Record<string, any>>} Fallback payload with refreshed GitHub profile and repo metadata.
 */
async function buildGithubEnhancedFallbackData() {
  const fallbackUsername =
    profileFallback?.github?.profile?.username ||
    profileFallback?.contact?.github?.split("/").pop() ||
    "Ramdragneel01";

  const [profileResult, reposResult] = await Promise.allSettled([
    fetch(`https://api.github.com/users/${fallbackUsername}`),
    fetch(`https://api.github.com/users/${fallbackUsername}/repos?sort=updated&per_page=100&type=owner`),
  ]);

  const profileResponse = profileResult.status === "fulfilled" ? profileResult.value : null;
  const reposResponse = reposResult.status === "fulfilled" ? reposResult.value : null;
  const profilePayload = profileResponse?.ok ? await profileResponse.json() : null;
  const reposPayload = reposResponse?.ok ? await reposResponse.json() : [];

  if (!profilePayload && (!Array.isArray(reposPayload) || !reposPayload.length)) {
    throw new Error("GitHub fallback request failed");
  }

  const normalizedRepos = Array.isArray(reposPayload)
    ? reposPayload.map((repo) => ({
        id: String(repo.id),
        title: repo.name,
      description: repo.description || "",
        readmeSummary: "",
        github: repo.html_url,
        tags: [repo.language, ...(repo.topics || [])].filter(Boolean).slice(0, 4),
        stars: repo.stargazers_count || 0,
        forks: repo.forks_count || 0,
        updatedAt: repo.updated_at || "",
      }))
    : [];

  return {
    ...profileFallback,
    github: {
      profile: {
        username: profilePayload?.login || fallbackUsername,
        avatarUrl: profilePayload?.avatar_url || "",
        followers: profilePayload?.followers || 0,
        following: profilePayload?.following || 0,
        publicRepos:
          profilePayload?.public_repos ||
          normalizedRepos.length ||
          profileFallback?.github?.profile?.publicRepos ||
          27,
        bio: profilePayload?.bio || "",
        profileUrl: profilePayload?.html_url || `https://github.com/${fallbackUsername}`,
      },
      repos: normalizedRepos.length ? normalizedRepos : profileFallback?.github?.repos || [],
    },
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Determine whether an API payload needs GitHub repo hydration.
 *
 * @param {Record<string, any>} payload Profile payload from backend.
 * @returns {boolean} True when repository data is missing or suspiciously low.
 */
function shouldHydrateGithubRepos(payload) {
  const apiRepos = Array.isArray(payload?.github?.repos) ? payload.github.repos : [];
  const apiRepoCount = apiRepos.length;
  const publicRepoCount = Number(payload?.github?.profile?.publicRepos) || 0;

  if (!apiRepoCount) {
    return true;
  }

  if (publicRepoCount > 0 && apiRepoCount < Math.min(publicRepoCount, 12)) {
    return true;
  }

  return false;
}

/**
 * Load portfolio profile payload from backend and provide resilient fallback data.
 *
 * @returns {{data: Record<string, any>, loading: boolean, error: string}} Hook state.
 */
export default function usePortfolioData() {
  const [data, setData] = useState(profileFallback);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;
    let intervalId;

    /**
     * Load profile payload and optionally preserve current UI state on transient failures.
     *
     * @param {{silent?: boolean}} options Fetch behavior options.
     */
    async function loadProfile(options = {}) {
      const { silent = false } = options;

      try {
        const response = await fetch(buildApiUrl("/profile"));

        if (!response.ok) {
          throw new Error(`Profile request failed with status ${response.status}`);
        }

        const payload = await response.json();
        let resolvedPayload = payload;
        let hydrationApplied = false;

        if (shouldHydrateGithubRepos(payload)) {
          try {
            const enhancedFallback = await buildGithubEnhancedFallbackData();
            resolvedPayload = {
              ...payload,
              github: enhancedFallback.github,
              updatedAt: payload?.updatedAt || enhancedFallback.updatedAt,
            };
            hydrationApplied = true;
          } catch {
            hydrationApplied = false;
          }
        }

        if (!isMounted) {
          return;
        }

        setData(resolvedPayload);
        if (hydrationApplied && !silent) {
          setError(
            "Live profile API returned partial GitHub data. Recovered repository snapshot from public GitHub feed."
          );
        } else {
          setError("");
        }
      } catch (fetchError) {
        if (!isMounted || fetchError.name === "AbortError") {
          return;
        }

        if (!silent) {
          try {
            const enhancedFallback = await buildGithubEnhancedFallbackData();
            if (!isMounted) {
              return;
            }

            setData(enhancedFallback);
            setError(
              "Live profile API is unavailable. Showing fallback profile data with a live public GitHub snapshot."
            );
          } catch {
            setData(profileFallback);
            setError("Live profile data is unavailable right now. Showing fallback profile data.");
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadProfile();

    // Keep profile data near-real-time while respecting resource efficiency.
    intervalId = window.setInterval(() => {
      loadProfile({ silent: true });
    }, 45_000);

    /**
     * Refresh data when the tab becomes active again.
     */
    function handleForegroundRefresh() {
      if (document.visibilityState === "visible") {
        loadProfile({ silent: true });
      }
    }

    document.addEventListener("visibilitychange", handleForegroundRefresh);
    window.addEventListener("focus", handleForegroundRefresh);

    return () => {
      isMounted = false;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      document.removeEventListener("visibilitychange", handleForegroundRefresh);
      window.removeEventListener("focus", handleForegroundRefresh);
    };
  }, []);

  return { data, loading, error };
}
